/* eslint-disable react-hooks/exhaustive-deps, import/first */
import React, { useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';

import { clientState, disconnectClient, masterAddRoomActivePlayers, masterMarkRoomAsGameOver, sendPhotonEvent, setPhotonEventHandlers } from './utils/photon/client';
import {
  CHESS_PIECE,
  OFFLINE_THRESHOLD_MINUTES, PHOTON_PING_INTERVAL_SECONDS, SEPARATOR_PING_DATA,
  THRESHOLD_TIMERS_RESYNCH_SECONDS, THRESHOLD_TIMERS_WARNING_SECONDS,
  LOW_TIME_UI_MSG_BELOW_SECONDS,
  REMATCH_MSG_PREFIX,
} from './config/config';
import { IS_LOCAL } from './config/env';
import { getPlayerDetails, } from './utils/UI/players';
import { PlayersHeader } from './PlayersHeader';
import { formatTime, timers } from './utils/formatTime';
import { matchTracker, resetMatchTracker, updateSessionStats } from './utils/game/tracker';
import { postVictoryDiscord } from './utils/discord/notify';
import { opponentAbandonedGame } from './utils/photon/online';
import { DelayedRender } from './components/DelayedRender';
import { PostGameButtons } from './PostGameButtons';
import './Game.scss';

if (IS_LOCAL) {
  window.Chessboard = Chessboard;
}

export const Game = ({ appData, addMsg, handlers }) => {
  const { loggedInUser: currentUser, players = {}, isInviteMatch, room= {}, maxTime } = appData;
  const { player1, player2, masterName, masterColor } = players;
  const { roomID } = room;

  const { localPlayer, opponent } = getPlayerDetails({ currentUser, player1, player2, masterName, masterColor });
  const { name: localPlayerName, color: localPlayerColor } = localPlayer;
  const { name: opponentName, color: opponentColor } = opponent;
  const opponentColorShort = opponentColor.substring(0, 1);
  const localPlayerColorShort = localPlayerColor.substring(0, 1);

  // UI LOGS
  const addMsgWithTs = (msg) => {
    const msgTime = formatTime(((Date.now() - matchTracker.approxStartTime) / 1000).toFixed(0));
    addMsg(`${msgTime} - ${msg}`, { id: 'game' });
  };

  // TRACKER
  matchTracker.matchInfo = { localPlayer: localPlayerName, localPlayerColor, opponent: opponentName };

  // GAME LOGIC
  const game = useRef(new Chess());
  const [currentTurn, setCurrentTurn] = useState('w');
  const [colorInCheck, setColorInCheck] = useState(null); // UI only
  const [matchWinner, setMatchWinner] = useState(null);
  
  // CHESSBOARD
  const [position, setPosition] = useState(game.current.fen());
  
  // TIMERS
  const [whiteTime, setWhiteTime] = useState(maxTime);
  const [blackTime, setBlackTime] = useState(maxTime);
  const [expiringTimeColor, setExpiringTimeColor] = useState(null);
  const opponentTime = localPlayerColor === 'white' ? blackTime : whiteTime;
  const localPlayertime = localPlayerColor === 'white' ? whiteTime : blackTime;

  // Refs
  const myLastMoveTs = useRef(null); // offline status & auto-disconnect
  const activeTimerRef = useRef(null);
  const whiteTimeRef = useRef(whiteTime);
  const blackTimeRef = useRef(blackTime);


  // ## HANDLE WINNER

  const stopTimers = () => {
    clearInterval(activeTimerRef.current);
    activeTimerRef.current = null;
  };

  const handleMatchEnd = (winningUser, { opponentDisconnected = false } = {}) => {
    // REACTUI
    setMatchWinner(winningUser);
    // DISCORD
    const isMyVictory = winningUser === localPlayerName;
    const isDraw = winningUser === 'DRAW';
    const isMasterDraw = isDraw && localPlayerName === masterName;
    if (isMyVictory || isMasterDraw) {
      const isTimeout = opponentColor === 'white' ? (whiteTime <= 0) : (blackTime <= 0);
      const fen = game.current.fen();
      postVictoryDiscord({
        matchTracker, fen,
        whiteTime, blackTime,
        isDraw, isTimeout, opponentDisconnected,
      });
    }
    // MATCHES TRACKER
    updateSessionStats({ isMyVictory, isDraw, opponentName, localPlayerColor });
    // PHOTON: mark room as Game Over
    if (localPlayerName === masterName) masterMarkRoomAsGameOver();
    // CLEANUP
    clientState.playing = false;
    stopTimers();
    resetMatchTracker();
  };

  // ## INIT

  // onMount
  // -- setup game tracker
  // -- master sets room active players (for auto-kick)
  useEffect(() => {
    if (!matchTracker.approxStartTime) matchTracker.approxStartTime = Date.now();
    if (localPlayerName === masterName) masterAddRoomActivePlayers([localPlayerName, opponentName]);
    return () => resetMatchTracker();
  }, [])

  // onMount -- SEND PHOTON Pings: KEEPALIVE and TIMERS synch
  useEffect(() => {
    let pingNumber = 0;
    const autoPingId = setInterval(() => {
      // offline check
      const stoppedPlaying = myLastMoveTs.current
        && ((Date.now() - myLastMoveTs.current) > (OFFLINE_THRESHOLD_MINUTES * 60 * 1000));
      if (stoppedPlaying) {
        clearInterval(autoPingId);
        disconnectClient();
        return;
      }
      // online ping
      pingNumber++;
      const checksum = timers.encode(pingNumber, whiteTimeRef.current, blackTimeRef.current);
      sendPhotonEvent(
        1,
        { type: 'PING', from: currentUser, checksum },
      );
    }, PHOTON_PING_INTERVAL_SECONDS * 1000);
    return () => clearInterval(autoPingId);
  }, []);

  // onMount -- RECEIVE PHOTON Events
  useEffect(() => {
    // Listen For Photon Moves
    setPhotonEventHandlers({
      onEvent: (eventCode, content = {}) => {
        if (eventCode === 1) {
          if (matchWinner) return;
          const isOpponentPing = content.type === 'PING' && content.from === opponentName;
          if (!isOpponentPing) return;
          // use the opponent's timers received in ping if they're smaller
          const [_counter, whiteTimePing, blackTimePing] = timers
            .decode(content.checksum || '')?.split(SEPARATOR_PING_DATA);
          const whiteDiff = whiteTimeRef.current - whiteTimePing;
          if (whiteDiff > THRESHOLD_TIMERS_RESYNCH_SECONDS) {
            setWhiteTime(whiteTimePing);
            if (whiteDiff > THRESHOLD_TIMERS_WARNING_SECONDS) {
              addMsg('White player time had to be re-synched..', { id: 'game' });
            }
          }
          const blackDiff = blackTimeRef.current - blackTimePing;
          if (blackDiff > THRESHOLD_TIMERS_RESYNCH_SECONDS) {
            setBlackTime(blackTimePing);
            if (blackDiff > THRESHOLD_TIMERS_WARNING_SECONDS) {
              addMsg('Black player time had to be re-synched..', { id: 'game' });
            }
          }
        } else if (eventCode === 2) {
          if (matchWinner) return;
          handleMoveFromPhoton(content);
        } else if (eventCode === 3) {
          addMsg(`${content.msg}`, { id: 'opponent-msg' });
        } else if (eventCode === 4) {
          addMsg(`${REMATCH_MSG_PREFIX}${opponentName} ${
            content.rematch ? 'asked for a REMATCH' : 'left'}`, { id: 'rematch' });
        }
      },
      onJoinRoom: () => {
        addMsg(`${currentUser} (re?)joined the room`, { id: 'game' });
      },
    });

    // onUnmount cleanup
    return () => stopTimers();
  }, []);


  // ## TIMERS

  // onStateChange -- Update local timers
  useEffect(() => {
    if (matchWinner) return;
    // Clear any previous interval when the turn changes
    if (activeTimerRef.current) stopTimers();
    // Start right timer
    if (currentTurn === 'w') {
      activeTimerRef.current = setInterval(() => {
        setWhiteTime((prev) => prev - 1);
        whiteTimeRef.current--;
        if (whiteTimeRef.current < LOW_TIME_UI_MSG_BELOW_SECONDS) setExpiringTimeColor('w'); // UI
      }, 1000);
    } else if (currentTurn === 'b') {
      activeTimerRef.current = setInterval(() => {
        setBlackTime((prev) => prev - 1);
        blackTimeRef.current--;
        if (blackTimeRef.current < LOW_TIME_UI_MSG_BELOW_SECONDS) setExpiringTimeColor('b'); // UI
      }, 1000);
    }
  }, [currentTurn]);

  // onStateChange -- Check Timeouts
  useEffect(() => {
    if (whiteTime <= 0) {
      handleMatchEnd(`${localPlayerColor === 'white' ? opponentName : localPlayerName}`); // white lost
    } else if (blackTime <= 0) {
      handleMatchEnd(`${localPlayerColor === 'black' ? opponentName : localPlayer}`); // black lost
    }
    // Check Offline Status
    if (opponentAbandonedGame()) {
      handleMatchEnd(localPlayerName, { opponentDisconnected: true });
    }
  }, [whiteTime, blackTime]);


  // ## GAME LOGIC

  // Handle piece drop on the chessboard
  const onPieceDrop = (sourceSquare, targetSquare, piece) => {
    myLastMoveTs.current = Date.now();
    // # {VALIDATE}
    if (sourceSquare === targetSquare) return false; // same square
    if (game.current.isGameOver()) return false; // game over
    // Moved when time is over
    if (whiteTime <=0 || blackTime <=0) return false;
    // Moved opponent's piece
    const movedPieceColor = piece[0];
    if (localPlayerColorShort !== movedPieceColor) return false;
    // Moved when it's not their turn
    if (
      (game.current.turn() === 'w' && piece.startsWith('b')) ||
      (game.current.turn() === 'b' && piece.startsWith('w'))
    ) return false;
    // # {MOVE}
    let move;
    try {
      move = game.current.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
    } catch (err) {
      console.error('Caught error validating move:', err);
      return false;
    }
    if (move === null) return false;
    // # PROCESS
    const isCheckmateMove = game.current.isCheckmate();
    // await sendMoveToHiveChain({ move, isCheckmateMove }); // push in same op @@ @@
    sendMoveToPhoton(move);
    // # {Track}
    matchTracker.moves.push(`${localPlayerColorShort}${CHESS_PIECE[move.piece] || '❓'}${move.lan}`);
    // # {UI}
    addMsgWithTs(`${currentUser} moved: ` + move.lan);
    // # {Update State}
    setPosition(game.current.fen()); // Update the local board (UI)
    setCurrentTurn(game.current.turn());
    checkGameCompleted({ isCheckmateMove });
    return true;
  };

  // Handle incoming moves from Photon
  const handleMoveFromPhoton = (moveData) => {
    // # {VALIDATE}
    if (matchWinner) return;
    // # {MOVE}
    try {
      game.current.move(moveData);
    } catch (err) {
      console.error('Received invalid move from Photon:', moveData);
      return;
    }
    // # {Track}
    matchTracker.moves.push(`${opponentColorShort}${CHESS_PIECE[moveData.piece] || '❓'}${moveData.lan}`);
    // # {UI}
    addMsgWithTs(`${opponentName} moved: ` + moveData.lan);
    // # {Update State}
    setPosition(game.current.fen()); // Update the local board (UI)
    setCurrentTurn(game.current.turn());
    checkGameCompleted();
  };

  // Send moves to the opponent via Photon
  const sendMoveToPhoton = (move) => {
    sendPhotonEvent(2, move); // Event code 2 for game moves
  };

  // Check the game status to determine the winner
  const checkGameCompleted = ({ isCheckmateMove } = {}) => {
    const isCheckmate = isCheckmateMove !== undefined ? isCheckmateMove : game.current.isCheckmate();
    if (isCheckmate) {
      const colorWinner = game.current.turn() === 'w' ? 'black' : 'white';
      handleMatchEnd(localPlayerColor === colorWinner ? localPlayerName : opponentName);
    } else if (game.current.isDraw() || game.current.isStalemate()) {
      handleMatchEnd('DRAW');
    }
    // UI only
    if (game.current.inCheck()) {
      const colorTurn = game.current.turn();
      setColorInCheck(colorTurn);
      setTimeout(
        () => setColorInCheck(null),
        (colorTurn === opponentColorShort ? 1 : 3) * 1000,
      )
    } else {
      setColorInCheck(null);
    }
  };

  return (
    <div id={`game-${roomID}`} className="game">
      <PlayersHeader
        position="top"
        user={opponentName}
        color={opponentColor}
        time={opponentTime}
        isRoomCreator={opponentName === masterName}
        inCheck={colorInCheck === opponentColorShort}
        inExpiration={expiringTimeColor === opponentColorShort}
        {...{ opponentName, currentTurn, matchWinner }}
      />

      <div className="chessboard">
        <Chessboard
          boardOrientation={localPlayerColor}
          position={position}
          onPieceDrop={onPieceDrop}
        />
      </div>

      <PlayersHeader
        position="bottom"
        user={currentUser}
        color={localPlayerColor}
        time={localPlayertime}
        isRoomCreator={currentUser === masterName}
        inCheck={colorInCheck === localPlayerColorShort}
        inExpiration={expiringTimeColor === localPlayerColorShort}
        {...{ opponentName, currentTurn, matchWinner }}
      />

      {matchWinner &&
        <DelayedRender delayMs={3000}>
          <PostGameButtons handlers={handlers}/>
        </DelayedRender>
      }
    </div>
  );
};
